import { apiClient } from './apiClient';
import firebaseConfigService from './firebaseConfig';

/**
 * Tenant context — branding, entitlements and which Firestore database this
 * tenant's data lives in.
 *
 * Fetched once after login from `GET /api/tenant/context`. Two things depend
 * on it landing before the app renders or subscribes to anything:
 *
 *  1. `productFlags.isFlagOn()` reads entitlements from here, so flags resolve
 *     per tenant instead of per build.
 *  2. The Firestore client SDK must be pointed at this tenant's database.
 *     Until that happens every client talks to `(default)` — TMT's data.
 *
 * Held in a module singleton rather than only in Redux because
 * `productFlags.js` is imported by non-React code and cannot read the store.
 */

let context = null;
let loadPromise = null;
const listeners = new Set();

/** The current tenant context, or null before login. */
export function getTenantContext() {
  return context;
}

/** Entitlements map, or an empty object before the context loads. */
export function getTenantFeatures() {
  return context?.features || {};
}

/** Branding, or an empty object before the context loads. */
export function getTenantBrand() {
  return context?.brand || {};
}

/** Per-tenant relabelling, e.g. { claim: 'Case' }. */
export function getTenantTerminology() {
  return context?.terminology || {};
}

/** Subscribe to context changes. Returns an unsubscribe function. */
export function onTenantContextChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function applyContext(next) {
  context = next;

  // Point the Firestore client SDK at this tenant's database *before* anything
  // subscribes. The backend serves databaseId for exactly this reason; it is
  // not a secret, because per-database security rules gate on the orgId claim.
  if (next?.databaseId) {
    firebaseConfigService.setTenantDatabase(next.databaseId);
  }

  listeners.forEach((fn) => {
    try {
      fn(next);
    } catch (err) {
      console.error('[tenant] context listener failed', err);
    }
  });
}

/**
 * Load the tenant context. Safe to call more than once — concurrent callers
 * share one request.
 *
 * Deliberately does not swallow failures into a default tenant: if we cannot
 * tell which tenant this is, flags should stay at their code defaults rather
 * than silently inheriting someone else's.
 */
export async function loadTenantContext({ force = false } = {}) {
  if (context && !force) return context;
  if (loadPromise && !force) return loadPromise;

  loadPromise = apiClient
    .get('/tenant/context')
    .then((res) => {
      const payload = res?.data?.data || res?.data;
      if (!payload?.orgId) {
        throw new Error('tenant context response had no orgId');
      }
      applyContext(payload);
      return payload;
    })
    .catch((err) => {
      console.error('[tenant] failed to load context', err);
      throw err;
    })
    .finally(() => {
      loadPromise = null;
    });

  return loadPromise;
}

/** Clear on logout so the next user does not inherit this tenant. */
export function clearTenantContext() {
  applyContext(null);
}

const tenantContextService = {
  getTenantContext,
  getTenantFeatures,
  getTenantBrand,
  getTenantTerminology,
  onTenantContextChange,
  loadTenantContext,
  clearTenantContext,
};

export default tenantContextService;
